import { LoadingService } from './../../../services/loading.service';
import { BountiesFiltersComponent } from './../bounties-filters/bounties-filters.component';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { Bounty } from '../../../interfaces/Bounty';
import { BountyService } from '../../../services/bounty.service';
import { UserService } from 'src/app/services/user.service';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { sortableFields } from 'src/app/resolvers/bounties.resolver';

export type Tabs = 'active' | 'completed' | 'liked';

@Component({
  selector: 'app-bounties-list',
  templateUrl: './bounties-list.component.html',
  styleUrls: ['./bounties-list.component.scss'],
})
export class BountiesListComponent implements OnInit, OnDestroy {
  public bounties: Bounty[];
  public activeCount: number;
  public completedCount: number;
  public likedCount: number;
  public page = 1;
  public moreToFetch: boolean;
  public selectedFilters = [];
  public fetching = false;
  public sortableFields = sortableFields;
  public selectedSort: string = this.sortableFields[0].value;
  public showInstructions = false;
  public allTabs: Tabs[];
  public activeTab: Tabs = 'active';
  public user: any;
  private subscriptions: Subscription[] = [];
  public searchInput: string;
  public searchTerm: string;

  @ViewChild('searchInputEl') searchInputEl;
  @ViewChild(BountiesFiltersComponent)
  filtersComponent: BountiesFiltersComponent;

  constructor(
    private bountyService: BountyService,
    userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private loadingService: LoadingService,
  ) {
    this.user = userService.getUser();
    this.allTabs = <Tabs[]>(
      ['active', 'completed'].concat(this.user ? ['liked'] : [])
    );
  }

  ngOnInit() {
    this.subscriptions.push(
      this.route.data.subscribe(({ data }) => {
        // Get the resolved data
        const {
          listResults: {
            bounties,
            activeCount,
            likedCount,
            completedCount,
            hasMorePages,
          },
          appliedFilters,
          appliedSort,
          appliedTab,
          search,
          page,
        } = data;

        this.selectedFilters = appliedFilters;
        this.bounties = bounties;
        this.activeCount = activeCount;
        this.completedCount = completedCount;
        this.likedCount = likedCount;
        this.selectedSort = appliedSort;
        this.activeTab = appliedTab;
        this.searchInput = search;
        this.searchTerm = search;
        this.moreToFetch = hasMorePages !== false;
        this.page = parseInt(page, 10) || 1;
      }),
    );

    window.addEventListener('scroll', this.onWindowScroll, true);
    // Subscribe to search input changes
    this.searchInputEl.valueChanges
      .pipe(debounceTime(1000))
      .subscribe(async value => {
        if (value === this.searchTerm) {
          return;
        }

        await this.onFiltersChanged({ search: value });
      });
  }

  public toggleFiltersSection() {
    this.filtersComponent.toggle();
  }

  toggleSortSelect() {
    document.querySelector('sui-select').dispatchEvent(new MouseEvent('click'));
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onWindowScroll, true);
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  public resetFilters() {
    this.selectedFilters = [];
    this.searchTerm = undefined;
    this.searchInput = undefined;
    this.onFiltersChanged();
  }

  public removeFilter(filter: string) {
    this.onFiltersChanged({
      filters: this.selectedFilters.filter(val => val !== filter),
    });
  }

  public addFilter(filter: string) {
    const filters = this.selectedFilters.includes(filter)
      ? this.selectedFilters
      : this.selectedFilters.concat([filter]);
    this.onFiltersChanged({ filters });
  }

  public clearSearch() {
    this.searchInput = undefined;
    this.onFiltersChanged({ search: '' });
  }

  async onFiltersChanged(data?) {
    const {
      tab = this.activeTab,
      filters = this.selectedFilters,
      sort = this.selectedSort,
      search = this.searchTerm,
      page = null,
    } = data || {};
    this.page = 1;
    this.moreToFetch = true;

    await this.router.navigate(['.'], {
      queryParams: {
        tab,
        filters: filters.join(','),
        sort,
        search,
        page,
      },
      queryParamsHandling: 'merge',
    });
  }

  onWindowScroll = async () => {
    const scrollBottom =
      document.body.scrollHeight - window.innerHeight - window.scrollY;
    if (scrollBottom < 130 && !this.fetching) {
      await this.nextPage();
      // ++this.page;
    }
  }

  public async nextPage() {
    if (!this.moreToFetch) {
      return;
    }
    const page = this.page + 1;
    this.fetching = true;

    const {
      bounties,
      activeCount,
      likedCount,
      completedCount,
      hasMorePages,
    } = await this.loadingService.loadWhileFetching(this.bountyService.list({
      page,
      filters: this.selectedFilters,
      sort: this.selectedSort,
      tab: this.activeTab,
      search: this.searchTerm,
    }));

    this.page = page;
    this.activeCount = activeCount;
    this.completedCount = completedCount;
    this.likedCount = likedCount;
    this.moreToFetch = hasMorePages !== false;

    if (this.bounties) {
      this.bounties.push(...(bounties || []));
    } else {
      this.bounties = bounties;
    }

    this.fetching = false;
  }

  public async changeTab(tab: Tabs) {
    await this.onFiltersChanged({ tab });
  }

  public totalLabelLoadingClass(count?: number) {
    return typeof count === 'number' ? '' : 'notched circle loading icon';
  }

  tabsCount(tab: Tabs) {
    switch (tab) {
      case 'active':
        return this.activeCount;
      case 'completed':
        return this.completedCount;
      case 'liked':
        return this.likedCount;
    }
  }
}
